import { useUserStore } from "store";

/**
 * Interface para o estado de refresh
 */
interface TokenRefreshState {
  isRefreshing: boolean;
  failedQueue: Array<{
    resolve: (token: string | null) => void;
    reject: (error: any) => void;
  }>;
}

/**
 * Estado global para controle de refresh
 */
let refreshState: TokenRefreshState = {
  isRefreshing: false,
  failedQueue: [],
};

/**
 * Inicializa ou retorna o estado de refresh
 */
function getRefreshState(): TokenRefreshState {
  return refreshState;
}

/**
 * Função para fazer refresh do token com proteção contra race conditions
 */
export async function performTokenRefresh(): Promise<string | null> {
  const state = getRefreshState();

  // Se já está fazendo refresh, aguarda
  if (state.isRefreshing) {
    return new Promise((resolve, reject) => {
      state.failedQueue.push({ resolve, reject });
    });
  }

  try {
    state.isRefreshing = true;

    // Importa dinamicamente para evitar dependência circular
    const { refreshAuthToken } = await import("@login");

    const auth = useUserStore.getState().auth;

    if (!auth?.refreshToken) {
      return null;
    }

    const refreshed = await refreshAuthToken(auth.refreshToken);

    // Atualiza store com novo token
    const { setUserData } = useUserStore.getState();
    setUserData({
      auth: {
        ...auth,
        token: refreshed.token,
        refreshToken: refreshed.refreshToken,
        expiresAt: refreshed.expiresAt,
      },
    });

    // Resolve todas as requisições pendentes
    state.failedQueue.forEach(({ resolve }) => {
      resolve(refreshed.token);
    });
    state.failedQueue = [];

    return refreshed.token;
  } catch (error) {
    // Rejeita todas as requisições pendentes
    state.failedQueue.forEach(({ reject }) => {
      reject(error);
    });
    state.failedQueue = [];

    throw error;
  } finally {
    state.isRefreshing = false;
  }
}

/**
 * Verifica se um token está expirado
 */
export function isTokenExpired(token: string | null): boolean {
  if (!token) return true;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const expirationTime = payload.exp * 1000; // Converte para milissegundos
    const currentTime = Date.now();

    // Considera expirado se faltar menos de 5 minutos
    return currentTime >= expirationTime - 300000;
  } catch {
    return true;
  }
}

/**
 * Obtém o token atual, fazendo refresh se necessário
 */
export async function getValidToken(): Promise<string | null> {
  const auth = useUserStore.getState().auth;

  if (!auth?.token) {
    return null;
  }

  // Se o token não está expirado, retorna ele
  if (!isTokenExpired(auth.token)) {
    return auth.token;
  }

  // Se está expirado, tenta fazer refresh
  if (auth.refreshToken) {
    try {
      return await performTokenRefresh();
    } catch {
      return null;
    }
  }

  return null;
}

/**
 * Limpa o estado de refresh (útil para logout)
 */
export function clearRefreshState(): void {
  refreshState = {
    isRefreshing: false,
    failedQueue: [],
  };
}
